/**
 * Linmere New York (18 Oct 2026): script of the "Dressed for the city" design, taken from the approved
 * prototypes (Home v9, Product v7) and wired to the theme:
 * - phone menu (full screen, background inert, smooth scroll paused)
 * - cart button opens the theme's cart drawer; the count follows every cart change and leaves out the
 *   hidden shipping protection line
 * - newsletter: checks the email before Shopify's customer form is sent
 * - in-page links (open the accordion they point at), hero video play / pause
 * - motion: Lenis smooth scroll, GSAP intros, word and line reveals, scrubbed statement, image reveals,
 *   magnetic buttons and the hero's pointer depth. Off with "reduce motion".
 * GSAP 3.12.5, ScrollTrigger and Lenis 1.1.18 load before this file (snippets/lnm-city-head.liquid).
 * window.lc exposes scrollTo() and revealGroup() for assets/lnm-city-product.js.
 */
(function () {
  var doc = document.documentElement;
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var coarse = window.matchMedia('(pointer: coarse)').matches;
  var hasGsap = typeof window.gsap !== 'undefined' && typeof window.ScrollTrigger !== 'undefined';
  var motionOn = hasGsap && !reduceMotion;
  var lenis = null;
  var lc = (window.lc = window.lc || {});

  /* iOS Safari applies :active to a tapped element only when the page listens to touchstart; an empty passive
     listener turns the pressed states on (mobile-native, 18 Oct 2026) */
  document.addEventListener('touchstart', function () {}, { passive: true });

  if (!document.querySelector('.lc')) {
    doc.classList.remove('has-motion');
    return;
  }

  function headerOffset() {
    var header = document.getElementById('lcSiteHeader');
    return -((header ? header.offsetHeight : 72) + 28);
  }

  lc.scrollTo = function (target) {
    if (!target) return;
    if (lenis) lenis.scrollTo(target, { offset: headerOffset() });
    else {
      var top = target.getBoundingClientRect().top + window.scrollY + headerOffset();
      window.scrollTo({ top: top, behavior: reduceMotion ? 'auto' : 'smooth' });
    }
  };
  lc.revealGroup = function () {};

  /* ---------- menu (works with or without motion) ---------- */
  var menuBtn = document.getElementById('lcMenuBtn');
  var menu = document.getElementById('lcMenu');
  if (menuBtn && menu) {
    var menuItems = menu.querySelectorAll('[data-menu-item]');
    var lastFocus = null;
    var setBackgroundInert = function (on) {
      ['#MainContent', 'footer', '.lc-bar', '#lcStickyBar'].forEach(function (sel) {
        var el = document.querySelector(sel);
        if (el) el.inert = on;
      });
    };
    var openMenu = function () {
      lastFocus = document.activeElement;
      menu.classList.add('is-open');
      menuBtn.classList.add('is-open');
      menuBtn.setAttribute('aria-expanded', 'true');
      menuBtn.setAttribute('aria-label', 'Close menu');
      doc.classList.add('lc-menu-open');
      setBackgroundInert(true);
      if (lenis) lenis.stop();
      if (motionOn) {
        gsap.fromTo(menuItems, { y: 36, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: 0.8, ease: 'power4.out', stagger: 0.07, overwrite: true });
      }
      var first = menu.querySelector('a');
      if (first) first.focus({ preventScroll: true });
    };
    var closeMenu = function () {
      menu.classList.remove('is-open');
      menuBtn.classList.remove('is-open');
      menuBtn.setAttribute('aria-expanded', 'false');
      menuBtn.setAttribute('aria-label', 'Open menu');
      doc.classList.remove('lc-menu-open');
      setBackgroundInert(false);
      if (lenis) lenis.start();
      if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
    };
    menuBtn.addEventListener('click', function () {
      if (menu.classList.contains('is-open')) closeMenu();
      else openMenu();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && menu.classList.contains('is-open')) closeMenu();
    });
    window.addEventListener('resize', function () {
      if (window.innerWidth > 900 && menu.classList.contains('is-open')) closeMenu();
    });
    /* phone menu groups (Women, Men, Shoes) open in place to their subcategories */
    menu.querySelectorAll('.lc-m-toggle').forEach(function (b) {
      b.addEventListener('click', function () {
        var list = document.getElementById(b.getAttribute('aria-controls'));
        var open = b.getAttribute('aria-expanded') !== 'true';
        b.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (list) list.hidden = !open;
      });
    });
  }

  /* ---------- header dropdowns: the button opens them; a mouse hover too; Escape and outside clicks close ---------- */
  var navItems = Array.prototype.slice.call(document.querySelectorAll('[data-lc-nav]'));
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var closeDropdowns = function (except) {
    navItems.forEach(function (it) {
      if (it === except) return;
      var p = it.querySelector('.lc-dd');
      var t = it.querySelector('.lc-nav-toggle');
      if (p) p.hidden = true;
      if (t) t.setAttribute('aria-expanded', 'false');
    });
  };
  navItems.forEach(function (it) {
    var btn = it.querySelector('.lc-nav-toggle');
    var panel = it.querySelector('.lc-dd');
    var timer = 0;
    if (!btn || !panel) return;
    var open = function () { closeDropdowns(it); panel.hidden = false; btn.setAttribute('aria-expanded', 'true'); };
    var close = function () { panel.hidden = true; btn.setAttribute('aria-expanded', 'false'); };
    btn.addEventListener('click', function () { if (panel.hidden) open(); else close(); });
    if (finePointer) {
      it.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') { clearTimeout(timer); open(); } });
      it.addEventListener('pointerleave', function (e) { if (e.pointerType === 'mouse') { timer = setTimeout(close, 160); } });
    }
    it.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !panel.hidden) { close(); btn.focus(); } });
    it.addEventListener('focusout', function (e) { if (!it.contains(e.relatedTarget)) close(); });
  });
  if (navItems.length) {
    document.addEventListener('click', function (e) { if (!e.target.closest || !e.target.closest('[data-lc-nav]')) closeDropdowns(); });
  }

  /* ---------- cart: open the theme's drawer, keep the count right ---------- */
  var cartBtn = document.getElementById('lcCartBtn');
  var cartCount = document.getElementById('lcCartCount');
  if (cartBtn) {
    cartBtn.addEventListener('click', function (e) {
      var drawer = document.getElementById('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        e.preventDefault();
        drawer.open();
      }
    });
  }
  var protectionId = cartBtn ? Number(cartBtn.dataset.lcPv) || 0 : 0;
  var refreshCount = function () {
    var routes = (window.Theme && window.Theme.routes) || {};
    var url = (routes.cart_url || '/cart') + '.js';
    fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var count = 0;
        (cart.items || []).forEach(function (item) {
          if (item.variant_id !== protectionId) count += item.quantity;
        });
        if (cartCount) cartCount.textContent = String(count);
        if (cartBtn) cartBtn.setAttribute('aria-label', 'Cart, ' + count + (count === 1 ? ' item' : ' items'));
      })
      .catch(function () {});
  };
  document.addEventListener('shopify:cart:lines-update', function (event) {
    var settle = function () { setTimeout(refreshCount, 150); };
    if (event.promise && event.promise.then) event.promise.then(settle, settle);
    else settle();
  });
  window.addEventListener('pageshow', function (e) { if (e.persisted) refreshCount(); });

  /* ---------- newsletter (Shopify customer form) ---------- */
  var form = document.getElementById('lcNl');
  var email = document.getElementById('lcNlEmail');
  var msg = document.getElementById('lcNlMsg');
  var nlBtn = document.getElementById('lcNlBtn');
  if (form && email && msg) {
    form.addEventListener('submit', function (e) {
      var v = email.value.trim();
      var ok = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v);
      if (!ok) {
        e.preventDefault();
        email.setAttribute('aria-invalid', 'true');
        msg.className = 'lc-msg';
        msg.textContent = v ? 'That email address does not look right. Check it and try again.' : 'Enter your email address to sign up.';
        email.focus();
        return;
      }
      email.removeAttribute('aria-invalid');
      if (nlBtn) nlBtn.textContent = 'Adding';
    });
    email.addEventListener('input', function () {
      if (email.getAttribute('aria-invalid')) {
        email.removeAttribute('aria-invalid');
        msg.textContent = '';
      }
    });
  }

  /* ---------- in-page anchors (open the accordion they point at) ---------- */
  document.addEventListener('click', function (e) {
    var a = e.target.closest ? e.target.closest('.lc a[href^="#"]') : null;
    if (!a) return;
    var id = a.getAttribute('href').slice(1);
    var target = id ? document.getElementById(id) : null;
    if (!target) return;
    e.preventDefault();
    if (target.tagName === 'DETAILS') target.open = true;
    lc.scrollTo(target);
  });

  /* ---------- hero video: plays only with motion on; the button pauses it ---------- */
  var heroVideo = document.getElementById('lcHeroImg');
  var heroPlay = document.getElementById('lcHeroPlay');
  if (heroVideo && heroVideo.tagName === 'VIDEO' && heroPlay) {
    var setPlaying = function (on) {
      heroPlay.setAttribute('aria-pressed', on ? 'true' : 'false');
      heroPlay.setAttribute('aria-label', on ? 'Pause the video' : 'Play the video');
    };
    if (!reduceMotion) {
      heroVideo.play().catch(function () { setPlaying(false); });
    } else {
      setPlaying(false);
    }
    heroPlay.addEventListener('click', function () {
      if (heroVideo.paused) {
        heroVideo.play().then(function () { setPlaying(true); }).catch(function () { setPlaying(false); });
      } else {
        heroVideo.pause();
        setPlaying(false);
      }
    });
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) heroVideo.pause();
      else if (heroPlay.getAttribute('aria-pressed') === 'true') heroVideo.play().catch(function () {});
    });
  }

  if (!motionOn) {
    doc.classList.remove('has-motion');
    return;
  }

  /* ---------- motion system ---------- */
  window.lcMotionStarted = true;
  doc.classList.add('has-motion');
  gsap.registerPlugin(ScrollTrigger);
  gsap.defaults({ ease: 'power3.out', duration: 0.85 });

  if (typeof window.Lenis !== 'undefined') {
    lenis = new Lenis({
      lerp: 0.08,
      smoothWheel: true,
      wheelMultiplier: 0.9,
      // the cart drawer, search and other dialogs scroll on their own
      prevent: function (node) {
        return !!(node && node.closest && node.closest('dialog, [data-lenis-prevent], .lc-menu, .lc-subcats, .lc-active-chips'));
      }
    });
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add(function (time) { lenis.raf(time * 1000); });
    gsap.ticker.lagSmoothing(0);
    window.lnmLenis = lenis;
    // the theme locks the page with html[scroll-lock] while the cart drawer or a dialog is open
    new MutationObserver(function () {
      if (doc.hasAttribute('scroll-lock') || doc.classList.contains('lc-menu-open')) lenis.stop();
      else lenis.start();
    }).observe(doc, { attributes: true, attributeFilter: ['scroll-lock', 'class'] });
  }

  function splitWords(el) {
    if (el.dataset.split === 'true') return [];
    var text = el.textContent || '';
    var parts = text.split(/(\s+)/);
    el.textContent = '';
    el.setAttribute('aria-label', text.trim());
    var words = [];
    parts.forEach(function (part) {
      if (!part.trim()) {
        el.appendChild(document.createTextNode(part));
        return;
      }
      var mask = document.createElement('span');
      var word = document.createElement('span');
      mask.className = 'lc-motion-word-mask';
      mask.setAttribute('aria-hidden', 'true');
      word.className = 'lc-motion-word';
      word.textContent = part;
      mask.appendChild(word);
      el.appendChild(mask);
      words.push(word);
    });
    el.dataset.split = 'true';
    return words;
  }

  function wordsIn(el) {
    var words = splitWords(el);
    gsap.set(el, { autoAlpha: 1 });
    gsap.fromTo(words, { yPercent: 110, autoAlpha: 0 }, {
      yPercent: 0, autoAlpha: 1, duration: 0.9, ease: 'power4.out', stagger: 0.055,
      scrollTrigger: { trigger: el, start: 'top 84%', once: true }
    });
  }

  function revealGroup(group) {
    var items = group.querySelectorAll('[data-reveal-item]');
    gsap.set(group, { autoAlpha: 1 });
    if (!items.length) return;
    gsap.fromTo(items, { y: 34, autoAlpha: 0 }, {
      y: 0, autoAlpha: 1, duration: 0.95, ease: 'power4.out', stagger: 0.08,
      scrollTrigger: { trigger: group, start: 'top 82%', once: true }
    });
  }

  function revealOne(el) {
    gsap.fromTo(el, { y: 24, autoAlpha: 0 }, {
      y: 0, autoAlpha: 1, duration: 0.9, ease: 'power4.out', delay: Number(el.dataset.revealDelay || 0),
      scrollTrigger: { trigger: el, start: 'top 86%', once: true }
    });
  }

  /* a section that fills in later (recommendations): reveal it once it is on the page */
  lc.revealGroup = function (section) {
    section.querySelectorAll('[data-motion-text="words"]').forEach(function (el) { if (el.dataset.split !== 'true') wordsIn(el); });
    section.querySelectorAll('[data-reveal-group]').forEach(revealGroup);
    section.querySelectorAll('[data-reveal]:not([data-reveal-item])').forEach(revealOne);
    ScrollTrigger.refresh();
  };

  function start() {
    var heroFig = document.getElementById('lcHeroFig');
    var mainFig = document.getElementById('lcMainFig');

    if (heroFig) {
      /* hero intro: image first, headline lines, copy, CTA last */
      var intro = gsap.timeline({ defaults: { ease: 'power4.out' } });
      intro
        .fromTo('#lcHeroFig', { clipPath: 'inset(0 0 100% 0)' }, { clipPath: 'inset(0 0 0% 0)', duration: 1.2 }, 0.05)
        .fromTo('#lcHeroImg', { scale: 1.1 }, { scale: 1, duration: 1.6 }, 0.05)
        .fromTo('.lc-hero-line', { yPercent: 110 }, { yPercent: 0, duration: 1.0, stagger: 0.12 }, 0.4)
        .fromTo('.lc-hero-eyebrow', { y: 14, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: 0.7 }, 0.55)
        .fromTo('.lc-hero-sub', { y: 18, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: 0.8 }, 0.8)
        .fromTo('.lc-hero-cta', { y: 18, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: 0.8 }, 0.95)
        .fromTo('.lc-hero-proof', { y: 12, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: 0.7 }, 1.1);

      /* hero parallax (image only, text stays put) */
      gsap.to('#lcHeroImg', {
        y: function () { return -Math.round(window.innerHeight * 0.1); },
        ease: 'none',
        scrollTrigger: { trigger: '.lc-hero', start: 'top top', end: 'bottom top', scrub: 1.2, invalidateOnRefresh: true }
      });
    }

    if (mainFig) {
      /* product intro: photo settles in, then the buy box lines in reading order */
      var pintro = gsap.timeline({ defaults: { ease: 'power4.out' } });
      pintro
        .fromTo('#lcMainFig', { clipPath: 'inset(0 0 100% 0)' }, { clipPath: 'inset(0 0 0% 0)', duration: 1.1 }, 0.05)
        .fromTo('#lcMainImg', { scale: 1.08 }, { scale: 1, duration: 1.4, clearProps: 'transform' }, 0.05)
        .fromTo('#lcThumbs .lc-thumb', { x: -12, autoAlpha: 0 }, { x: 0, autoAlpha: 1, duration: 0.6, stagger: 0.05 }, 0.5)
        .fromTo('.lc-buy > *', { y: 18, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: 0.8, stagger: 0.07 }, 0.35);
    }

    /* section headings: word by word */
    gsap.utils.toArray('[data-motion-text="words"]').forEach(function (el) {
      if (el.closest('[hidden]')) return;
      wordsIn(el);
    });
    gsap.utils.toArray('[data-motion-text="lines"]').forEach(function (el) {
      var lines = el.querySelectorAll('.lc-motion-line');
      gsap.set(el, { autoAlpha: 1 });
      gsap.fromTo(lines, { yPercent: 100, autoAlpha: 0 }, {
        yPercent: 0, autoAlpha: 1, duration: 1, ease: 'power4.out', stagger: 0.11,
        scrollTrigger: { trigger: el, start: 'top 84%', once: true }
      });
    });

    /* statement: words move from muted to ink as you scroll through it */
    gsap.utils.toArray('[data-scrub-words]').forEach(function (el) {
      var words = splitWords(el);
      gsap.fromTo(words, { color: '#8E8880' }, {
        color: '#151412', ease: 'none', stagger: 0.06,
        scrollTrigger: { trigger: el, start: 'top 78%', end: 'bottom 42%', scrub: 0.9 }
      });
    });

    /* reveals, once */
    gsap.utils.toArray('[data-reveal-group]').forEach(function (group) {
      if (group.closest('[hidden]')) return;
      revealGroup(group);
    });
    gsap.utils.toArray('[data-reveal]:not([data-reveal-item])').forEach(function (el) {
      if (el.closest('[hidden]')) return;
      revealOne(el);
    });

    /* category tiles: clip open from the bottom, image settles */
    gsap.utils.toArray('[data-image-reveal]').forEach(function (fig, i) {
      var img = fig.querySelector('img, svg');
      gsap.set(fig, { autoAlpha: 1 });
      var tl = gsap.timeline({ scrollTrigger: { trigger: fig, start: 'top 84%', once: true } });
      tl.fromTo(fig, { clipPath: 'inset(0 0 100% 0)' }, { clipPath: 'inset(0 0 0% 0)', duration: 1.1, ease: 'power4.out', delay: i * 0.1 });
      if (img) tl.fromTo(img, { scale: 1.08 }, { scale: 1, duration: 1.3, ease: 'power4.out', clearProps: 'transform' }, '<');
    });

    /* magnetic CTAs and a barely-there pointer depth on the hero image (fine pointers only) */
    if (!coarse) {
      gsap.utils.toArray('[data-magnetic]').forEach(function (el) {
        var strength = 0.16;
        var xTo = gsap.quickTo(el, 'x', { duration: 0.45, ease: 'power3.out' });
        var yTo = gsap.quickTo(el, 'y', { duration: 0.45, ease: 'power3.out' });
        el.addEventListener('pointermove', function (e) {
          var r = el.getBoundingClientRect();
          xTo((e.clientX - r.left - r.width / 2) * strength);
          yTo((e.clientY - r.top - r.height / 2) * strength);
        });
        el.addEventListener('pointerleave', function () { xTo(0); yTo(0); });
      });
      gsap.utils.toArray('[data-mouse-parallax]').forEach(function (section) {
        var layer = section.querySelector('[data-mouse-depth]');
        if (!layer) return;
        var depth = Number(layer.dataset.mouseDepth || 0.012);
        var xTo = gsap.quickTo(layer, 'x', { duration: 0.9, ease: 'power3.out' });
        var last = 0;
        section.addEventListener('pointermove', function (e) {
          var now = performance.now();
          if (now - last < 32) return;
          last = now;
          var r = section.getBoundingClientRect();
          xTo((e.clientX - r.left - r.width / 2) * depth);
        });
        section.addEventListener('pointerleave', function () { xTo(0); });
        window.addEventListener('blur', function () { xTo(0); });
      });
    }

    /* keep measurements honest */
    window.addEventListener('load', function () { ScrollTrigger.refresh(); });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { ScrollTrigger.refresh(); });
  }

  document.addEventListener('visibilitychange', function () {
    if (document.hidden) gsap.ticker.sleep();
    else gsap.ticker.wake();
  });
  window.addEventListener('pagehide', function (e) {
    if (e.persisted) return;
    ScrollTrigger.getAll().forEach(function (t) { t.kill(); });
    if (lenis) lenis.destroy();
  });

  /* the product page script (a module) builds the thumbnails first; modules run before DOMContentLoaded */
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
